import React from 'react';
import { InputProvider } from './InputContext';
import * as S from './styles';
import { InputBaseProps, InputBaseStyleProps } from './types';

function ForwardedInputBase<
  As extends React.ElementType = 'div',
  TInput extends React.ElementType = 'input',
>(
  {
    as,
    borderless,
    className,
    inputComponent,
    inputComponentProps,
    endAdornment,
    startAdornment,
    validationState = 'valid',
    inputRef,
    ...rest
  }: InputBaseProps<As, TInput>,
  ref: React.ForwardedRef<React.ElementRef<As>>,
) {
  const { isDisabled } = rest;
  const Component: React.ElementType = as ?? 'div';

  const styleProps: InputBaseStyleProps = {
    $borderless: !!borderless,
    $hasEndAdornment: !!endAdornment,
    $hasStartAdornment: !!startAdornment,
    $validationState: validationState,
  };

  const InputComponent: React.ElementType = inputComponent ?? 'input';

  return (
    <InputProvider validationState={validationState}>
      <S.InputBaseRoot
        as={Component}
        className={className}
        data-disabled={isDisabled}
        data-invalid={validationState === 'invalid'}
        ref={ref}
        {...styleProps}
        {...rest}
      >
        {startAdornment}
        <S.Input
          ref={inputRef}
          as={InputComponent}
          {...styleProps}
          {...inputComponentProps}
        />
        {endAdornment}
      </S.InputBaseRoot>
    </InputProvider>
  );
}

export const InputBase = React.forwardRef(ForwardedInputBase) as <
  As extends React.ElementType = 'div',
  TInput extends React.ElementType = 'input',
>(
  props: InputBaseProps<As, TInput> & { ref?: React.Ref<React.ElementRef<As>> },
) => React.ReactElement | null;

export default InputBase;
